import { useEffect, useRef } from 'react';

const safetyServices = [
  {
    icon: 'ri-radar-line',
    title: '석면위해성 평가',
    subtitle: 'Risk Assessment',
    desc: '건축물 내 석면 함유 자재의 손상 상태, 위치, 비산 가능성 등을 종합 평가하여 위해 등급을 산출하고 최적의 관리 방안을 수립합니다.',
    items: ['석면 자재 손상도 및 비산 가능성 평가(년 2회 평가)', '건물 이용 현황 및 거주자 노출 위험도 분석', '위해 등급성(낮음/중간/높음) 판정', '관리 우선순위 결정 및 관리 계획 수립'],
    bg: 'bg-green-950',
    dark: true,
  },
  {
    icon: 'ri-bar-chart-box-line',
    title: '석면건축물 농도 측정',
    subtitle: 'Concentration Measurement',
    desc: '석면건축물 안전관리인 선임 의무 건물에 대해 정기적으로 공기 중 석면 농도를 측정하여 안전 기준 준수 여부를 확인하고 관리합니다.',
    items: ['2년 1회 이상 정기 공기 중 석면 농도 측정', '오염원, 시설 이용 빈도 등을 고려하여 측정', '0.01개/cc 기준 초과 여부 판정', '측정 결과 보고서 제출 및 기록 유지'],
    bg: 'bg-stone-50',
    dark: false,
  },
];

const managementFlow = [
  { icon: 'ri-search-line', label: '석면 조사' },
  { icon: 'ri-file-list-line', label: '위해성 평가' },
  { icon: 'ri-user-settings-line', label: '안전관리인 선임' },
  { icon: 'ri-calendar-check-line', label: '정기 점검' },
  { icon: 'ri-bar-chart-2-line', label: '농도 측정' },
  { icon: 'ri-book-open-line', label: '기록 보존' },
];

export default function SafetySection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.section-fade-in').forEach((el, i) => {
              setTimeout(() => el.classList.add('visible'), i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="safety" className="py-24 bg-stone-50" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="section-fade-in mb-16">
          <span className="inline-block bg-amber-100 text-amber-800 text-xs font-semibold px-4 py-1.5 rounded-full mb-4 tracking-widest uppercase">
            Safety Management
          </span>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <h2 className="text-3xl lg:text-4xl font-bold text-green-950 leading-tight whitespace-nowrap">
              석면건축물 안전관리
            </h2>
            <p className="text-stone-500 text-base max-w-md leading-relaxed">
              석면을 제거하지 않고 유지·관리하는 건축물에 대해
              지속적인 안전관리 서비스를 제공합니다.
            </p>
          </div>
        </div>

        {/* 안전관리 프로세스 — PC만 표시 */}
        <div className="section-fade-in mb-14 hidden md:block">
          <div className="bg-white rounded-3xl p-8 border border-stone-100">
            <h3 className="text-base font-semibold text-stone-400 mb-6 tracking-wide">안전관리 프로세스</h3>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              {managementFlow.map((item, i) => (
                <div key={item.label} className="flex items-center gap-2">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-12 h-12 flex items-center justify-center bg-green-50 border border-green-100 rounded-xl">
                      <i className={`${item.icon} text-green-700 text-lg`}></i>
                    </div>
                    <span className="text-xs text-stone-500 font-medium whitespace-nowrap">{item.label}</span>
                  </div>
                  {i < managementFlow.length - 1 && (
                    <i className="ri-arrow-right-line text-stone-300 text-lg mb-4"></i>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {safetyServices.map((service, idx) => (
            <div key={service.title} className={`section-fade-in ${service.bg} rounded-3xl overflow-hidden`} style={{ transitionDelay: `${idx * 150}ms` }}>
              <div className="p-8 lg:p-10">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-500/20 rounded-xl mb-5">
                  <i className={`${service.icon} ${service.dark ? 'text-amber-400' : 'text-amber-600'} text-2xl`}></i>
                </div>
                <div className="mb-1">
                  <span className={`text-xs font-semibold tracking-widest ${service.dark ? 'text-amber-400/70' : 'text-amber-600/70'}`}>
                    {service.subtitle}
                  </span>
                </div>
                <h3 className={`text-2xl font-bold mb-4 ${service.dark ? 'text-white' : 'text-green-950'}`}>
                  {service.title}
                </h3>
                <p className={`text-sm leading-relaxed mb-6 ${service.dark ? 'text-white/60' : 'text-stone-500'}`}>
                  {service.desc}
                </p>
                <ul className="space-y-3">
                  {service.items.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <div className={`w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${service.dark ? 'bg-amber-500/20' : 'bg-green-100'}`}>
                        <i className={`ri-check-line text-xs ${service.dark ? 'text-amber-400' : 'text-green-700'}`}></i>
                      </div>
                      <span className={`text-sm leading-relaxed ${service.dark ? 'text-white/80' : 'text-stone-600'}`}>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative h-52 overflow-hidden">
                <img
                  src={
                    idx === 0
                      ? "https://public.readdy.ai/ai/img_res/fc7706e71e4343689e68bea569bbf31a.jpg"
                      : "https://readdy.ai/api/search-image?query=air%20quality%20monitoring%20equipment%20testing%20machine%20environmental%20measurement%20device%20laboratory%20professional%20scientific%20instruments%20measuring%20air%20particulate%20asbestos%20fiber%20concentration%20clean%20indoor%20environment&width=800&height=400&seq=asb-measure-007&orientation=landscape"
                  }
                  alt={service.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className={`absolute inset-0 ${service.dark ? 'bg-green-950/40' : 'bg-stone-200/30'}`}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
